// Final Mix — build (offline render + MP3), preview, download, publish.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Disc3, Download, Send, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { StudioEngine } from "../../lib/studioEngine";
import { renderMixdown, encodeMp3 } from "../../lib/studioMixdown";
import { PublishDialog } from "./PublishDialog";

const fmt = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

export function FinalMixPanel({ projectId, dirty }) {
  const [status, setStatus] = useState(null); // publish-status
  const [busy, setBusy] = useState(null); // stage text
  const [error, setError] = useState(null);
  const [mix, setMix] = useState(null); // {url, blob, durationMs, peakDb, rmsDb, fingerprint, project, vocalClips}
  const [showPublish, setShowPublish] = useState(false);
  const urlRef = useRef(null);

  const loadStatus = () => api.get(`/studio/projects/${projectId}/publish-status`).then((r) => { setStatus(r.data); return r.data; });
  useEffect(() => { loadStatus().catch(() => {}); }, [projectId]); // eslint-disable-line
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const build = async () => {
    setError(null);
    setBusy("Preparing…");
    let engine = null;
    try {
      const [{ data }, st] = await Promise.all([api.get(`/studio/projects/${projectId}`), loadStatus()]);
      const p = data.project;
      const beatUrl = p.beat_source === "marketplace" ? p.beat_ref?.audio_url : p.beat_ref?.upload_url;
      if (!beatUrl) throw new Error("Add a beat in Studio first");
      engine = new StudioEngine(beatUrl);
      const out = await renderMixdown({ engine, project: p, tracks: data.tracks || [], takes: data.takes || [], onStage: setBusy, fxAllowed: !!data.studio_pro });
      setBusy("Encoding MP3… 0%");
      const blob = await encodeMp3({ left: out.left, right: out.right, onProgress: (n) => setBusy(`Encoding MP3… ${n}%`) });
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      setMix({ ...out, left: null, right: null, blob, url: urlRef.current, fingerprint: st.fingerprint, project: p });
      toast.success("Final mix ready — give it a listen");
    } catch (e) {
      setError(e?.message || "The final mix failed — nothing was saved or published");
    } finally {
      try { engine?.dispose(); } catch { /* */ }
      setBusy(null);
    }
  };

  const safeName = (mix?.project?.title || "rgm-song").replace(/[^\w\- ]+/g, "").trim() || "rgm-song";
  const stale = mix && status && status.fingerprint !== mix.fingerprint;
  const published = status?.already_published_this_mix;

  return (
    <section id="final-mix" className="mt-10 rounded-2xl border border-amber-300/25 bg-gradient-to-b from-amber-400/[0.07] to-transparent p-4 sm:p-5" data-testid="final-mix-panel">
      <div className="flex items-center gap-2">
        <Disc3 className="w-5 h-5 text-amber-300" />
        <h2 className="text-base md:text-lg font-black">Final Mix</h2>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        Beat + every audible vocal track with your edits, mixer and mastering — rendered on this device. Your Studio project and versions stay untouched.
      </p>

      {!mix && (
        <button type="button" onClick={build} disabled={!!busy}
          className="mt-4 w-full h-12 rounded-xl bg-gradient-to-b from-fuchsia-500 to-purple-700 ring-1 ring-amber-300/40 font-black text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="final-mix-build-btn">
          {busy ? <><RefreshCw className="w-4 h-4 animate-spin" />{busy}</> : "Build final mix"}
        </button>
      )}
      {busy && <p className="mt-2 text-[11px] text-amber-200" data-testid="final-mix-stage">{busy} Keep this screen open.</p>}
      {error && (
        <p className="mt-3 text-xs text-red-300 flex gap-1.5 items-start" data-testid="final-mix-error"><AlertTriangle className="w-4 h-4 shrink-0" />{error}</p>
      )}

      {mix && (
        <div className="mt-4 space-y-3" data-testid="final-mix-result">
          <audio controls src={mix.url} className="w-full" data-testid="final-mix-audio" />
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Length" value={fmt(mix.durationMs)} testid="final-mix-length" />
            <Stat label="Peak" value={`${mix.peakDb.toFixed(1)} dB`} warn={mix.peakDb > -0.1} testid="final-mix-peak" />
            <Stat label="Vocal clips" value={String(mix.vocalClips)} testid="final-mix-clips" />
          </div>
          {mix.peakDb > -0.1 && <p className="text-[11px] text-amber-300" data-testid="final-mix-clip-warning">Some peaks hit 0 dB — lower a vocal or the beat, or use a master preset with the limiter.</p>}
          {stale && <p className="text-[11px] text-amber-300" data-testid="final-mix-stale">The project changed since this mix — rebuild before publishing.</p>}
          <div className="flex flex-wrap gap-2">
            <a href={mix.url} download={`${safeName}.mp3`} className="flex-1 h-11 rounded-xl border border-white/15 hover:bg-white/5 font-bold text-sm flex items-center justify-center gap-2" data-testid="final-mix-download-btn">
              <Download className="w-4 h-4" />Download MP3
            </a>
            <button type="button" onClick={build} disabled={!!busy} className="h-11 px-4 rounded-xl border border-white/15 hover:bg-white/5 font-bold text-sm flex items-center gap-2 disabled:opacity-50" data-testid="final-mix-rebuild-btn">
              <RefreshCw className="w-4 h-4" />Rebuild
            </button>
          </div>
          <button type="button" disabled={!!busy || stale || published}
            onClick={() => (dirty ? toast.error("Save your project details first") : setShowPublish(true))}
            className="w-full h-12 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-black text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="publish-to-rgm-btn">
            <Send className="w-4 h-4" />{published ? "This mix is already published" : "Publish to RGM"}
          </button>
        </div>
      )}

      {(status?.publications || []).length > 0 && (
        <div className="mt-4 space-y-1" data-testid="publication-history">
          {status.publications.slice().reverse().map((x) => (
            <a key={x.submission_id} href={`/track/${x.submission_id}`} className="flex items-center gap-2 text-xs text-emerald-300 hover:underline" data-testid={`publication-${x.submission_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" />Published {new Date(x.published_at).toLocaleString()} · {fmt(x.duration_ms || 0)}
            </a>
          ))}
        </div>
      )}

      <PublishDialog open={showPublish} onClose={() => setShowPublish(false)} projectId={projectId} mix={mix}
        onPublished={() => loadStatus()} />
    </section>
  );
}

function Stat({ label, value, warn, testid }) {
  return (
    <div className="rounded-xl bg-black/40 border border-white/10 py-2">
      <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
      <p className={`text-sm font-black tabular-nums ${warn ? "text-amber-300" : "text-white"}`} data-testid={testid}>{value}</p>
    </div>
  );
}
